"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  ARABIC_SYSTEMS,
  type ArabicOptions,
  arabicLetterRows,
  romanizeArabic,
  type TaMarbuta,
} from "@/lib/tool-kit/transliteration-more/arabic";

/** English strings. A translated content file overrides these through `ui`. */
export const ARABIC_TRANSLITERATOR_STRINGS = {
  inputLabel: "Arabic text",
  inputHint:
    "Typed, pasted or copied from a web page. Vowel marks (fatḥah, ḍammah, kasrah, sukūn, shaddah) are used when they are there.",
  samples: {
    vocalised: "كِتَابُ الْجَبْرِ وَالْمُقَابَلَةِ",
    unvocalised: "محمد بن موسى الخوارزمي",
  },
  sampleVocalised: "Sample with vowel marks",
  sampleUnvocalised: "Sample without vowel marks",
  optionsLegend: "Options",
  pausal:
    "Pausal form: leave out final case vowels and tanwīn (ALA-LC rule 14, DMG rule 10)",
  capitalize: "Capitalize each word, except the article al-",
  taMarbutaLabel: "Tāʾ marbūṭa (ة)",
  taMarbutaOptions: {
    default: "Default — h (ALA-LC), a (DMG, simple)",
    t: "t — construct state (Wizārat al-Tarbiyah)",
  },
  resultsHeading: "Romanized",
  systems: {
    "ala-lc": "ALA-LC (Library of Congress)",
    din: "DMG / DIN 31635",
    simple: "Simple (no diacritics)",
  },
  uses: {
    "ala-lc":
      "Library catalogs and most English-language reference lists, following the 2012 table.",
    din: "Academic work in Arabic and Islamic studies: one symbol per sound, no digraphs.",
    simple: "Names and words for general readers, with no special letters.",
  },
  enterText: "Type or paste some Arabic to romanize.",
  unvocalisedNotice:
    "one:{count} word has no vowel marks. Only consonants and long vowels can be shown for it — short vowels cannot be recovered from the consonants, and a و or ي is read as the long vowel ū or ī, so bayt appears as bīt. Add the vowel marks to the Arabic for a complete result.|other:{count} words have no vowel marks. Only consonants and long vowels can be shown for them — short vowels cannot be recovered from the consonants, and a و or ي is read as the long vowel ū or ī, so bayt appears as bīt. Add the vowel marks to the Arabic for a complete result.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Consonants, side by side",
  letterColumn: "Letter",
  alaColumn: "ALA-LC",
  dinColumn: "DMG / DIN 31635",
  simpleColumn: "Simple",
  tableNote:
    "Vowels: fatḥah a, ḍammah u, kasrah i in every system. The long vowels ā ū ī are written with a macron (a u i in the simple form), and alif maqṣūrah is á in ALA-LC and ā in DMG.",
} as const;

type Strings = typeof ARABIC_TRANSLITERATOR_STRINGS;

const DEFAULT_OPTIONS: ArabicOptions = {
  pausal: true,
  taMarbuta: "default",
  capitalize: true,
};

function LetterTable({ strings }: { strings: Strings }) {
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <DataTable
        head={[
          strings.letterColumn,
          strings.alaColumn,
          strings.dinColumn,
          strings.simpleColumn,
        ]}
        rows={arabicLetterRows().map((row) => [row.arabic, ...row.values])}
      />
      <p className="text-muted-foreground text-xs">{strings.tableNote}</p>
    </section>
  );
}

export default function ArabicTransliteratorWidget() {
  const strings = useToolUi(ARABIC_TRANSLITERATOR_STRINGS);
  const locale = useToolLocale();
  const [text, setText] = useState<string>(strings.samples.vocalised);
  const [options, setOptions] = useState<ArabicOptions>(DEFAULT_OPTIONS);

  const results = ARABIC_SYSTEMS.map((system) => ({
    system,
    result: romanizeArabic(text, system, options),
  }));
  const unvocalised = results[0]?.result.unvocalised ?? 0;
  const empty = text.trim() === "";

  return (
    <div className="space-y-6">
      <ScriptTextArea
        dir="rtl"
        hint={strings.inputHint}
        id="ar-input"
        label={strings.inputLabel}
        lang="ar"
        onChange={setText}
        value={text}
      />
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() => setText(strings.samples.vocalised)}
          type="button"
        >
          {strings.sampleVocalised}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setText(strings.samples.unvocalised)}
          type="button"
        >
          {strings.sampleUnvocalised}
        </button>
      </div>
      <fieldset className="space-y-3">
        <legend className={LABEL}>{strings.optionsLegend}</legend>
        <Checkbox
          checked={options.pausal}
          label={strings.pausal}
          onChange={(pausal) => setOptions((o) => ({ ...o, pausal }))}
        />
        <Checkbox
          checked={options.capitalize}
          label={strings.capitalize}
          onChange={(capitalize) => setOptions((o) => ({ ...o, capitalize }))}
        />
        <div className="max-w-md">
          <SelectField<TaMarbuta>
            id="ar-ta"
            label={strings.taMarbutaLabel}
            onChange={(taMarbuta) => setOptions((o) => ({ ...o, taMarbuta }))}
            options={[
              { value: "default", label: strings.taMarbutaOptions.default },
              { value: "t", label: strings.taMarbutaOptions.t },
            ]}
            value={options.taMarbuta}
          />
        </div>
      </fieldset>
      {empty ? (
        <Notice>{strings.enterText}</Notice>
      ) : (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
          {unvocalised > 0 ? (
            <Notice>
              {tPlural(strings.unvocalisedNotice, locale, unvocalised)}
            </Notice>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            {results.map(({ system, result }) => (
              <ResultCard
                copiedLabel={strings.copied}
                copyLabel={strings.copy}
                key={system}
                lang="ar-Latn"
                text={result.text}
                title={strings.systems[system]}
                use={strings.uses[system]}
              />
            ))}
          </div>
        </section>
      )}
      <LetterTable strings={strings} />
    </div>
  );
}
